import { motion } from "framer-motion";
import { Home, FileText, CarFront, Users, Briefcase, Handshake, FileSignature, Landmark } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.8, ease: EASE } }),
};

const FAMILY_FEATURES = [
  { icon: Home, label: "Buying or selling a home", text: "Have your purchase documents reviewed before you sign — not after something goes wrong." },
  { icon: FileText, label: "Wills & estate basics", text: "Get a standard will prepared and keep it updated as your life changes." },
  { icon: CarFront, label: "Traffic tickets & accidents", text: "Moving violations, accidents, and disputes — get advice before you pay or plead." },
  { icon: Users, label: "The whole household", text: "Family plans typically extend coverage to your spouse and dependent children." },
];

const FAMILY_ACCORDION = [
  { q: "What does 'unlimited advice' actually mean?", a: "You can call your provider law firm about any personal legal matter — no per-question fee, no watching the clock. If it's outside the plan, they'll tell you upfront what it would cost." },
  { q: "Can a lawyer really make calls for me?", a: "Yes. Plans commonly include letters and phone calls made on your behalf to resolve disputes — a landlord, a warranty claim, a billing error. A letter on law-firm letterhead changes conversations." },
  { q: "What if I actually end up in court?", a: "Plans include a set of trial defense hours that grow the longer you're a member, plus discounted rates for matters beyond the plan's scope." },
];

const BIZ_FEATURES = [
  { icon: FileSignature, label: "Vendor & client contracts", text: "Get contracts reviewed before you commit. Catch the clause that would have cost you." },
  { icon: Landmark, label: "Debt collection letters", text: "Formal demand letters from a law firm get unpaid invoices taken seriously." },
  { icon: Handshake, label: "HR & hiring questions", text: "Guidance on hiring, terminations, and workplace policy before small issues become claims." },
  { icon: Briefcase, label: "Business trial defense", text: "Coverage hours for covered civil claims against your business, plus discounted rates beyond the plan." },
];

const BIZ_ACCORDION = [
  { q: "Is this a replacement for having an attorney on retainer?", a: "Think of it as a retainer you can actually afford. Routine consultations, document review, and correspondence are covered by the membership — you only pay extra for large, out-of-scope matters, at member rates." },
  { q: "What size business is this for?", a: "Plans are designed for small businesses — from solo operators to teams with employees. The right tier depends on your headcount and how much contract traffic you have." },
  { q: "How fast can I actually talk to someone?", a: "Routine questions are typically answered within business hours by your designated provider firm. Business emergencies have expedited channels." },
];

function Track({ id, eyebrow, title, image, imageAlt, features, accordion, flipped, testid }) {
  return (
    <div id={id} className="py-20 md:py-28 scroll-mt-20" data-testid={testid}>
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start`}>
        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className={`lg:col-span-5 relative ${flipped ? "lg:order-2" : ""}`}
        >
          <div className="absolute -inset-4 bg-blue-600/10 blur-2xl rounded-[2.5rem]" />
          <div className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-xl shadow-blue-950/10">
            <img src={image} alt={imageAlt} className="w-full h-[420px] object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/55 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-6 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-sky-300">{eyebrow}</p>
          </div>
        </motion.div>

        <div className={`lg:col-span-7 ${flipped ? "lg:order-1" : ""}`}>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={0}
            className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-4">{eyebrow}</motion.p>
          <motion.h3 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={1}
            className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-[#0B192C] leading-tight mb-10">{title}</motion.h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
            {features.map((f, i) => (
              <motion.div
                key={f.label}
                variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i + 1}
                className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100"
                data-testid={`${testid}-feature-${i}`}
              >
                <f.icon size={22} className="text-blue-600 mb-4" strokeWidth={1.8} />
                <p className="font-display font-bold text-[#0B192C] mb-2">{f.label}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={2}>
            <Accordion type="single" collapsible className="w-full" data-testid={`${testid}-accordion`}>
              {accordion.map((item, i) => (
                <AccordionItem key={i} value={`${testid}-${i}`} className="border-slate-200">
                  <AccordionTrigger data-testid={`${testid}-faq-trigger-${i}`} className="text-left font-display font-semibold text-[#0B192C] hover:text-blue-700 hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function Audiences() {
  return (
    <section className="bg-[#F8FAFC] border-y border-slate-200" data-testid="audiences-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <Track
          id="families"
          eyebrow="For individuals & families"
          title="Everyday life has fine print. Read it with a lawyer."
          image="https://images.unsplash.com/photo-1666866834805-8cc91d4774ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxjb25maWRlbnQlMjBidXNpbmVzcyUyMGNvbnN1bHRhbnQlMjBzbWlsZSUyMGJsdWV8ZW58MHx8fHwxNzg4NzI3ODI2fDA&ixlib=rb-4.1.0&q=85"
          imageAlt="Associate consulting with a family"
          features={FAMILY_FEATURES}
          accordion={FAMILY_ACCORDION}
          testid="families-track"
        />
        <div className="h-px bg-slate-200" />
        <Track
          id="business"
          eyebrow="For small business owners"
          title="Your business deserves legal backup that fits the budget."
          image="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBidXNpbmVzcyUyMG93bmVyJTIwdGVhbSUyMG1lZXRpbmd8ZW58MHx8fHwxNzg4NzI3ODI2fDA&ixlib=rb-4.1.0&q=85"
          imageAlt="Small business team reviewing contracts"
          features={BIZ_FEATURES}
          accordion={BIZ_ACCORDION}
          flipped
          testid="business-track"
        />
      </div>
    </section>
  );
}
